// author: Sanjid Islam Chowdhury
import axios from 'axios'
import Overlay from './overlay.js'
import delegate from './delegate'

class Modal {
	modalTriggers = []
	entranceAnimation = 'animate__fadeIn'
	exitAnimation = 'animate__fadeOut'

	constructor() {
		// bind this to all methods
		this.openModal = this.openModal.bind(this)
		this.closeModal = this.closeModal.bind(this)
		this.createModal = this.createModal.bind(this)
		this.parseElement = this.parseElement.bind(this)
		this.populateModal = this.populateModal.bind(this)
		this.updateModalInstances = this.updateModalInstances.bind(this)


		this.initModalBody()

		// search for all modal triggers that does not have data-modal-lazy-loading attribute
		this.createModal('[data-modal-trigger]:not([data-modal-lazy-loading="true"])')

		// listen for new modal triggers added to the dom
		delegate({
			selector: '[data-modal-trigger]',
			event: 'click',
			handler: (element) => {
				if (!this.modalTriggers.includes(element)) {
					// dynamically added modal
					this.createModal(element, { cached: false })
				}
				element.openModal()
			}
		})
	}

	oneOff(linkOrSelector, options) {
		const a = document.createElement('a')
		a.dataset.modalTrigger = linkOrSelector
		document.body.appendChild(a)
		a.click()
		a.onModalLoad = () => {
			options?.onModalLoad?.call(a)
			a.remove()
		}
	}

	updateModalInstances() {
		this.modalTriggers = []
		this.createModal('[data-modal-trigger]')
	}

	createModal(selector, options) {
		// if selector is a string, then it's a selector,
		// if it is a jquery object, then it's a jquery object
		let elements;
		if (typeof selector === 'string')
			elements = document.querySelectorAll(selector)
		else if (selector instanceof jQuery) {
			elements = selector

			// turn selector into an array of dom elements
			elements = elements.get()
		} else if (selector instanceof HTMLElement) {
			// if element is a dom element, then it's a dom element
			elements = [selector]
		} else if (selector instanceof NodeList) {
			elements = selector
		}

		if (elements) {
			elements.forEach(element => {
				this.parseElement(element, { ...options, modalInstanceIndex: this.modalTriggers.length })
				this.modalTriggers.push(element)
			})
		}
	}

	parseElement(element, { route, cached = true, modalInstanceIndex }) {
		element.modalInstanceIndex = modalInstanceIndex
		element.setAttribute('data-modal-instance', modalInstanceIndex)

		if (route) {
			element.setAttribute('data-modal-trigger', route)
		}

		if (element.getAttribute('data-modal-trigger').match(/^(http|https):\/\//)) {
			// check if route is a url with regex
			element.isExternal = true
		} else {
			element.isExternal = false
		}

		element.openModal = () => {
			this.openModal(element)
		}

		element.closeModal = () => {
			this.closeModal(element)
		}

		element.modalInstance = null

		if (element.isExternal) {
			if (cached) {
				axios.get(element.getAttribute('data-modal-trigger')).then(response => {
					element.modalContent = response.data
				})
			}
		} else {
			element.modalContent = document.querySelector(element.getAttribute('data-modal-trigger'))
		}
	}

	openModal(modalElement) {
		// fire an event
		const event = new CustomEvent('modalOpen', {
			detail: {
				modalCaller: modalElement
			}
		})
		document.dispatchEvent(event)

		const route = modalElement.getAttribute('data-modal-trigger')
		Overlay.show()

		this.attachModalToDocument()

		window.__.currentModalCaller = modalElement
		modalElement.modalInstance = this.wrapper

		if (modalElement.onModalOpen)
			modalElement.onModalOpen()

		this.body.querySelector('.modal__close').addEventListener('click', e => {
			e.preventDefault()
			this.closeModal(modalElement)
		})

		Overlay.onClick(() => {
			this.closeModal(modalElement)
		})

		if (!modalElement.modalContent) {
			if (modalElement.isExternal)
				axios.get(route).then(response => this.populateModal(modalElement, response.data))
			else
				this.populateModal(modalElement, document.querySelector(route) || '')
		} else {
			this.populateModal(modalElement, modalElement.modalContent)
		}

		// check for info-sliders, if there is any, then close them
		const infoSliders = document.querySelectorAll('.info-slider')
		infoSliders.forEach(infoSlider => {
			infoSlider.close()
		})
	}

	populateModal(modalElement, data) {
		const modalContent = this.body.querySelector('.modal__content')
		const htmlTemplate = data
		modalContent.setAttribute('data-modal-instance', modalElement.modalInstanceIndex)
		modalContent.innerHTML = ''

		if (htmlTemplate instanceof HTMLElement) {
			const node = modalElement.dataset.modalClone === 'true'
				? htmlTemplate.cloneNode(true)
				: htmlTemplate
			this.sanitizeModalContent(node)
			modalContent.appendChild(node)
		} else {
			const parsed = this.parseModalHtml(String(htmlTemplate))
			while (parsed.firstChild) {
				modalContent.appendChild(parsed.firstChild)
			}
		}

		this.executeScripts(modalContent)
		// remove the spinner
		this.body.querySelector('.spinner')?.remove()

		// callback
		if (modalElement.onModalLoad)
			modalElement.onModalLoad()

		const event = new CustomEvent('modalLoad', {
			detail: {
				modalCaller: modalElement,
				modalContent: modalContent
			}
		})
		document.dispatchEvent(event)

	}

	parseModalHtml(html) {
		const doc = new DOMParser().parseFromString(html, 'text/html')
		this.sanitizeModalContent(doc.body)
		return doc.body
	}

	sanitizeModalContent(root) {
		if (!root?.querySelectorAll) {
			return
		}

		if (root.hasAttribute?.('style')) {
			root.removeAttribute('style')
		}

		root.querySelectorAll('[style]').forEach(el => el.removeAttribute('style'))
		root.querySelectorAll('style:not([nonce])').forEach(el => el.remove())
	}

	executeScripts(container) {
		const nonce = window.__CSP_NONCE__
		const scripts = Array.from(container.querySelectorAll('script'))
		scripts.forEach(oldScript => {
			const newScript = document.createElement('script')
			Array.from(oldScript.attributes).forEach(attr => {
				newScript.setAttribute(attr.name, attr.value)
			})
			if (nonce) {
				newScript.nonce = nonce
			}
			if (oldScript.src) {
				newScript.src = oldScript.src
			} else {
				const code = oldScript.textContent?.trim()
				if (!code) {
					oldScript.remove()
					return
				}
				// Wrap inline scripts so reopening a modal does not redeclare const/let in global scope.
				const isModule = oldScript.getAttribute('type') === 'module'
				newScript.textContent = isModule ? code : `(function(){\n${code}\n})();`
			}
			oldScript.parentNode.replaceChild(newScript, oldScript)
		})
	}

	closeModal(modalElement) {
		if (modalElement.onModalClose)
			modalElement.onModalClose()

		window.__.currentModalCaller = null
		modalElement.modalInstance = null

		setTimeout(() => {
			Overlay.hide()
		}, 100)

		// give a little animation 
		this.body.classList.remove(this.entranceAnimation)
		this.body.classList.add(this.exitAnimation)
		setTimeout(() => {
			this.wrapper.remove()
			this.initModalBody()
		}, 500)
	}

	initModalBody() {
		this.body = document.createElement('div')
		this.wrapper = document.createElement('div')
		this.wrapper.classList.add('modal__wrapper')
		this.body.classList.remove(this.entranceAnimation, this.exitAnimation)
		this.body.classList.add('modal', 'animate__animated', this.entranceAnimation)

		this.body.innerHTML = `
			<span class="material-symbols-rounded modal__close">close</span>
			<div class="modal__content">
				<div class="lds-ring spinner">
					<div></div>
					<div></div>
					<div></div>
					<div></div>
				</div>
			</div>
		`

		this.wrapper.appendChild(this.body)
	}

	attachModalToDocument() {

		// set top and left to scroll position
		this.wrapper.style.top = `${window.scrollY}px`;
		this.wrapper.style.left = `${window.scrollX}px`;
		document.body.appendChild(this.wrapper)
	}

}

export default Modal;

